import React, { useState, useEffect, useCallback } from 'react';
import { X, ShieldCheck } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { Pill } from './Pill';
import { EmptyState } from './EmptyState';
import { listMyOAuthConsents, revokeMyOAuthConsent } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

/**
 * MyAuthorizedAppsPanel — the signed-in user's own view of the applications they've allowed to act
 * on their behalf against governed agents. Distinct from the admin "Connected Applications" list
 * (which registers OAuth clients): this is self-service consent management. Withdrawing an
 * application immediately revokes its live access. Reuses the existing consent endpoints.
 */
function firstArray(res, key) {
  const d = res && (res.data !== undefined ? res.data : res);
  if (Array.isArray(d)) return d;
  if (d && Array.isArray(d[key])) return d[key];
  return [];
}

function whenLabel(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString();
}

export default function MyAuthorizedAppsPanel() {
  const { addToast } = useToast();
  const [consents, setConsents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmingId, setConfirmingId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listMyOAuthConsents();
      setConsents(firstArray(r, 'consents'));
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not load your applications', description: e?.message });
    } finally {
      setLoading(false);
    }
  }, [addToast]);

  useEffect(() => { reload(); }, [reload]);

  const withdraw = async (id) => {
    setBusy(true);
    try {
      const res = await revokeMyOAuthConsent(id);
      if (res && res.ok === false) {
        addToast({ tone: 'danger', title: 'Could not withdraw access', description: res.error || res.data?.error });
        return;
      }
      addToast({ tone: 'success', title: 'Access withdrawn', description: 'The application can no longer act on your behalf.' });
      setConfirmingId(null);
      await reload();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Could not withdraw access', description: e?.message });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2">
      <div>
        <h3 className="text-[15px] font-semibold text-v2-ink m-0">Applications you've authorized</h3>
        <p className="text-[12.5px] text-v2-ink-secondary mt-1 mb-0">
          Applications you've allowed to act on your behalf against protected agents. Withdraw one to
          revoke its access immediately.
        </p>
      </div>

      <Card padding={loading || consents.length === 0 ? 'md' : 'none'}>
        {loading ? (
          <p className="text-[13px] text-v2-ink-secondary p-2 m-0">Loading…</p>
        ) : consents.length === 0 ? (
          <EmptyState
            icon={<ShieldCheck size={20} />}
            title="No applications authorized"
            description="When you allow an application to act for you, it will appear here so you can review or withdraw it."
          />
        ) : (
          <ul className="divide-y divide-v2-border">
            {consents.map((c) => {
              const confirming = confirmingId === c.id;
              const granted = whenLabel(c.granted_at);
              return (
                <li key={c.id} className="flex items-center gap-3 px-3 py-2.5 text-[13px]">
                  <span className="min-w-0">
                    <span className="font-semibold text-v2-ink truncate">{c.application_name || 'Application'}</span>
                    {c.agent_name && <span className="text-v2-ink-secondary"> · acting on {c.agent_name}</span>}
                    {granted && <span className="block text-[11.5px] text-v2-ink-muted">Authorized {granted}</span>}
                  </span>
                  <span className="ml-auto flex items-center gap-1.5">
                    {confirming ? (
                      <>
                        <span className="text-[11.5px] text-v2-ink-muted">Withdraw?</span>
                        <Button size="xs" variant="danger" disabled={busy} onClick={() => withdraw(c.id)}>Yes</Button>
                        <Button size="xs" variant="ghost" disabled={busy} onClick={() => setConfirmingId(null)}>No</Button>
                      </>
                    ) : (
                      <Button size="xs" variant="ghost" disabled={busy} iconLeft={<X size={13} />}
                        onClick={() => setConfirmingId(c.id)}>Withdraw</Button>
                    )}
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </div>
  );
}
